import { Alert, Button } from "@mui/material";
import { Box } from "@mui/system";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { RBDItemType, RBDTransmissionType, RBDType } from "../../app/config/enum";
import { IRBDElement, IRBDItem } from "../../app/models/rbd";
import { useStore } from "../../app/stores/store";

function RBDSettingAction() {
    const { rbdStore, commonStore } = useStore();
    const { selectedItemType, selectedSystem, selectedEquipment, selectedTransmissionType, selectedTransmissionItems } = rbdStore;
    const [addButtonDisabled, setAddButtonDisabled] = useState(false);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [btnErrorMessage, setBtnErrorMessage] = useState("");

    const setAddButtonState = (disabled: boolean, message: string) => {
        setAddButtonDisabled(disabled);
        setBtnErrorMessage(message);
    }

    const setSaveButtonState = (disabled: boolean, message: string) => {
        setSaveButtonDisabled(disabled);
        setBtnErrorMessage(message);
    }

    useEffect(() => {
        if (!rbdStore.isEdit) { // Disable Add Button (kalo add new)
            setAddButtonState(false, "");
            if (rbdStore.selectedItemType === RBDItemType.ELEMENT) { // kalau type elemen
                if (!rbdStore.selectedSystem) // kalau system ga kepilih, ga bisa add
                    setAddButtonState(true, "Please select equipment");
                else if (rbdStore.selectedRbdType === RBDType.SYSTEM && !rbdStore.selectedEquipment) // kalau RBD punya system, equipment harus dipilih
                    setAddButtonState(true, "Please select equipment");
            } else { // kalau type transmission
                const selectedSeqs = [...rbdStore.selectedTransmissionItemsSequence].sort((a, b) => a - b);
                const minSel = selectedSeqs[0];
                const maxSel = selectedSeqs[selectedSeqs.length - 1];
                const hasGapInSelection = selectedSeqs.length >= 2 && rbdStore.transmissionableItems()
                    .filter(x => x.sequence >= minSel && x.sequence <= maxSel)
                    .some(x => selectedSeqs.indexOf(x.sequence) < 0);

                if (rbdStore.selectedTransmissionItems.length < 2)
                    setAddButtonState(true, "Please select at least 2 elements");
                else if (hasGapInSelection)
                    setAddButtonState(true, "Elements in the transmission must be sequential");
                else if (rbdStore.selectedTransmissionType === RBDTransmissionType.SERIAL &&
                    rbdStore.selectedTransmissionItems.filter(x => x.transmission?.transmissionType === RBDTransmissionType.SERIAL).length > 0)
                    setAddButtonState(true, "Serial transmission cannot have Serial elements directly yet");
                else if (rbdStore.selectedTransmissionType === RBDTransmissionType.PARALLEL &&
                    rbdStore.selectedTransmissionItems.filter(x => x.transmission && [RBDTransmissionType.PARALLEL, RBDTransmissionType.SHARING_LOAD, RBDTransmissionType.REDUNDANT].includes(x.transmission.transmissionType)).length > 0)
                    setAddButtonState(true, "Parallel transmission cannot have Parallel / Sharing Load / Redundant elements directly");
                else if (rbdStore.selectedTransmissionType === RBDTransmissionType.SHARING_LOAD &&
                    rbdStore.selectedTransmissionItems.filter(x => x.transmission && [RBDTransmissionType.PARALLEL, RBDTransmissionType.SHARING_LOAD, RBDTransmissionType.REDUNDANT].includes(x.transmission.transmissionType)).length > 0)
                    setAddButtonState(true, "Sharing Load transmission cannot have Parallel / Sharing Load / Redundant elements directly");
                else if (rbdStore.selectedTransmissionType === RBDTransmissionType.REDUNDANT &&
                    rbdStore.selectedTransmissionItems.filter(x => x.transmission && [RBDTransmissionType.PARALLEL, RBDTransmissionType.SHARING_LOAD, RBDTransmissionType.REDUNDANT].includes(x.transmission.transmissionType)).length > 0)
                    setAddButtonState(true, "Redundant transmission cannot have Parallel / Sharing Load / Redundant elements directly");
            }
        } else { // Disable Save Button (kalo ngedit)
            setSaveButtonState(false, "");
            if (rbdStore.selectedItemType === RBDItemType.ELEMENT) { // kalau type elemen
                if (!rbdStore.selectedSystem) setSaveButtonState(true, "System has not been selected"); // kalau system ga kepilih, ga bisa add
                else if (rbdStore.selectedRbdType === RBDType.SYSTEM && !rbdStore.selectedEquipment) setSaveButtonState(true, "Equipment has not been selected"); // kalau RBD punya system, equipment harus dipilih
            }
        }

    }, [rbdStore, rbdStore.isEdit, rbdStore.selectedItemType, rbdStore.selectedSystem, rbdStore.selectedRbdType,
        rbdStore.selectedEquipment, rbdStore.selectedTransmissionType, rbdStore.selectedTransmissionItemsSequence,
        rbdStore.selectedTransmissionItems]);

    const handleAddItem = () => {
        let newItem: IRBDItem | null = null;
        let transmissionInsertAfterIndex: number | undefined;
        if (selectedItemType === RBDItemType.ELEMENT) {

            // elemen tergantung yg di add equipment / system
            let curElement: IRBDElement | null = null;
            if (selectedSystem && selectedEquipment) {
                curElement = {
                    systemId: selectedSystem.id,
                    equipmentId: selectedEquipment.id,
                    tagNumber: selectedEquipment.tagNumber,
                    equipmentType: selectedEquipment.equipmentType
                }
                rbdStore.setSelectedEquipment(null);
            } else if (selectedSystem) {
                curElement = {
                    systemId: selectedSystem.id,
                    name: selectedSystem.name
                }
                rbdStore.setSelectedSystem(null);
            }

            // bikin rbdItem nya
            if (curElement) {
                newItem = {
                    sequence: 0,
                    name: "",
                    itemType: RBDItemType.ELEMENT,
                    element: curElement,
                    isGrouped: false
                }
            }

        } else if (selectedItemType === RBDItemType.TRANSMISSION) {
            transmissionInsertAfterIndex = Math.max(...selectedTransmissionItems.map(x => x.sequence));
            rbdStore.setRbdItemGrouped(selectedTransmissionItems, true);

            // bikin rbdItem nya
            newItem = {
                sequence: 0,
                name: "",
                itemType: RBDItemType.TRANSMISSION,
                transmission: {
                    transmissionType: selectedTransmissionType,
                    elements: selectedTransmissionItems
                },
                isGrouped: false
            }

            // hapus selection (nanti di-reset pake reaction aja)
            rbdStore.setSelectedTransmissionItems([]);
        }

        // tambahin ke list di tabel
        if (newItem) {
            if (transmissionInsertAfterIndex !== undefined) {
                rbdStore.addRbdItem(newItem, transmissionInsertAfterIndex)
            } else {
                rbdStore.addRbdItem(newItem)
            }
        }
    }

    return (
        <Box sx={{ alignContent: "flex-end" }}>
            {!rbdStore.loadingTable && !rbdStore.loadingSystem && !commonStore.loadingStation && !rbdStore.loadingEquipment && btnErrorMessage &&
                <Box sx={{mb: '12px'}}>
                    <Alert severity="warning">{btnErrorMessage}</Alert>
                </Box>}
            <Box>
                {rbdStore.isEdit ?
                    (
                        <>
                            <Button variant="contained" sx={{ mr: 2 }} onClick={() => rbdStore.cancelEditRbdItem()} color="warning">Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={() => rbdStore.saveEditRbdItem()}
                                disabled={saveButtonDisabled}
                            >
                                Save &#8594;
                            </Button>
                        </>
                    ) :
                    (
                        <Button
                            variant="contained" onClick={() => handleAddItem()}
                            disabled={addButtonDisabled}
                        >
                            Add &#8594;
                        </Button>
                    )}
            </Box>
        </Box>
    );
}

export default observer(RBDSettingAction);